import type { I18n } from '@learnforge/i18n';
import { createSvgEl } from '@learnforge/demo-render';

export interface Point {
  x: number;
  y: number;
}

export interface TriangleOptions {
  vertices: [Point, Point, Point];
  showSideLabels?: ('a' | 'b' | 'c')[];
  showAngleArcs?: ('A' | 'B' | 'C')[];
  highlightAngle?: 'A' | 'B' | 'C';
  className?: string;
  i18n: I18n<string>;
}

export interface TriangleHandle {
  group: SVGGElement;
  update(opts: Partial<TriangleOptions>): void;
  destroy(): void;
}

export function createTriangle(parent: SVGGElement, opts: TriangleOptions): TriangleHandle {
  const group = createSvgEl('g', {
    class: ['tg-triangle-group', opts.className ?? ''].filter(Boolean).join(' '),
  });
  parent.appendChild(group);
  let current: TriangleOptions = { ...opts };

  function midpoint(p: Point, q: Point): Point {
    return { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };
  }

  function render(): void {
    while (group.firstChild) group.removeChild(group.firstChild);
    const [v1, v2, v3] = current.vertices;
    const path = createSvgEl('path', {
      class: 'tg-triangle',
      d: `M ${v1.x} ${v1.y} L ${v2.x} ${v2.y} L ${v3.x} ${v3.y} Z`,
      'vector-effect': 'non-scaling-stroke',
    });
    group.appendChild(path);

    const sides: Array<{ name: 'a' | 'b' | 'c'; from: Point; to: Point }> = [
      { name: 'a', from: v2, to: v3 }, // opposite A
      { name: 'b', from: v1, to: v3 }, // opposite B
      { name: 'c', from: v1, to: v2 }, // opposite C
    ];
    if (current.showSideLabels) {
      for (const side of sides) {
        if (!current.showSideLabels.includes(side.name)) continue;
        const m = midpoint(side.from, side.to);
        const dx = side.to.x - side.from.x;
        const dy = side.to.y - side.from.y;
        const len = Math.hypot(dx, dy) || 1;
        const ox = (-dy / len) * 0.35;
        const oy = (dx / len) * 0.35;
        const text = createSvgEl('text', {
          class: 'tg-side-label',
          x: m.x + ox,
          y: -(m.y + oy),
          'text-anchor': 'middle',
          'dominant-baseline': 'middle',
          transform: 'scale(1 -1)',
        });
        text.textContent = side.name;
        group.appendChild(text);
      }
    }
  }
  render();

  return {
    group,
    update(next) {
      current = { ...current, ...next };
      render();
    },
    destroy() {
      group.remove();
    },
  };
}
